/**
 * Sanitizes a string value by trimming whitespace and returning undefined if empty.
 */
const _sanitizeString = (str: string): string | undefined => {
  const trimmed = str.trim();
  return trimmed !== '' ? trimmed : undefined;
};

/**
 * Sanitizes an array by recursively sanitizing its items and removing undefined values.
 * Keeps empty arrays for backward compatibility.
 */
const _sanitizeArray = (arr: any[]): any[] => {
  return arr
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    .map(item => sanitizeOrderObject(item))
    .filter(item => item !== undefined);
};

/**
 * Sanitizes a single object property value based on its type.
 * Returns an object with the sanitized value and whether it should be kept.
 */
const _sanitizePropertyValue = (value: any): { sanitizedValue: any; shouldKeep: boolean } => {
  if (value === undefined) {
    return { sanitizedValue: undefined, shouldKeep: false };
  }

  // Handle arrays - always keep them (even if empty)
  if (Array.isArray(value)) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return { sanitizedValue: sanitizeOrderObject(value), shouldKeep: true };
  }

  // Recursively sanitize nested objects
  if (typeof value === 'object' && value !== null) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const sanitizedValue = sanitizeOrderObject(value);
    return { sanitizedValue, shouldKeep: sanitizedValue !== undefined };
  }

  // Trim strings and skip if empty
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return { sanitizedValue: trimmed, shouldKeep: trimmed !== '' };
  }

  // Keep non-string primitive values (numbers, booleans)
  return { sanitizedValue: value, shouldKeep: true };
};

/**
 * Sanitizes an object by processing each property and removing empty/undefined values.
 */
const _sanitizeObject = (obj: Record<string, any>): any => {
  const sanitized: any = {};
  let hasValidKeys = false;

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const { sanitizedValue, shouldKeep } = _sanitizePropertyValue(obj[key]);

      if (shouldKeep) {
        sanitized[key] = sanitizedValue;
        hasValidKeys = true;
      }
    }
  }

  return hasValidKeys ? sanitized : undefined;
};

/**
 * Recursively sanitizes an order object by:
 * - Removing properties with undefined values
 * - Removing empty objects (but keeping empty arrays for backward compatibility)
 * - Trimming string values and removing empty strings
 */
export const sanitizeOrderObject = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return undefined;
  }

  if (Array.isArray(obj)) {
    return _sanitizeArray(obj);
  }

  if (typeof obj === 'object') {
    return _sanitizeObject(obj);
  }

  if (typeof obj === 'string') {
    return _sanitizeString(obj);
  }

  return obj;
};
